import type { HttpError } from '@packages/domain/constants/http';
import { useSendOtp } from 'components/OtpVerificationModal/hooks/useSendOtp';
import { useSendOtpPayload } from 'hooks/useSendOtpPayload/useSendOtpPayload';
import { useCallback } from 'react';
import { otpSendCache } from 'services/payex/otp/otpSendCache';
import type { OtpChannel, OtpContext } from 'services/payex/otp/types';

type CheckEligibilityParams = {
  email: string;
  phone?: string;
  senderName?: string;
  recipientName?: string;
  context: OtpContext;
  channel: OtpChannel;
};

type EligibilityResult =
  | { status: 'verified' }
  | { status: 'needed' }
  | { status: 'failed'; error: HttpError };

const useOtpEligibility = () => {
  const sendOtp = useSendOtp();
  const buildPayload = useSendOtpPayload();

  const checkEligibility = useCallback(
    async (params: CheckEligibilityParams): Promise<EligibilityResult> => {
      const { email, phone, context, channel } = params;

      if (otpSendCache.wasRecentlySent({ email, phone, context, channel })) {
        return { status: 'needed' };
      }

      try {
        const data = await sendOtp.mutateAsync(buildPayload(params));

        if (data.alreadyVerified) {
          return { status: 'verified' };
        }

        otpSendCache.record({ email, phone, context, channel });
        return { status: 'needed' };
      } catch (error) {
        return { status: 'failed', error: error as HttpError };
      }
    },
    [sendOtp, buildPayload],
  );

  const needsVerification = useCallback(
    async (params: CheckEligibilityParams): Promise<boolean> =>
      (await checkEligibility(params)).status !== 'verified',
    [checkEligibility],
  );

  return {
    checkEligibility,
    needsVerification,
    isChecking: sendOtp.isPending,
  };
};

export type { EligibilityResult };
export { useOtpEligibility };
